/**
 * Orders List Page
 *
 * Displays a list of orders with filtering, search, and pagination.
 */

import { format, isValid, parseISO } from 'date-fns';
import { Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useOrders } from '../hooks';
import type { OrderStatus } from '../types';

const STATUS_OPTIONS: { value: OrderStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'reconciling', label: 'Reconciling' },
  { value: 'approved', label: 'Approved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function getStatusBadgeClass(status: OrderStatus): string {
  const classes: Record<OrderStatus, string> = {
    open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    reconciling: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
    approved: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  };
  return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
}

function formatDate(dateStr: string | null | undefined, formatStr: string = 'MMM d, yyyy'): string {
  if (!dateStr) return '-';
  try {
    const date = parseISO(dateStr);
    return isValid(date) ? format(date, formatStr) : '-';
  } catch {
    return '-';
  }
}

export default function OrdersListPage() {
  const navigate = useNavigate();
  const { setPageHeader, clearPageHeader } = usePageHeader();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OrderStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Set page header
  useEffect(() => {
    setPageHeader({
      title: 'Orders',
      breadcrumbs: [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Orders' }],
    });
    return () => clearPageHeader();
  }, [setPageHeader, clearPageHeader]);

  const { data, isLoading, error } = useOrders({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    page,
    limit,
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1); // Reset to first page on search
  };

  const handleStatusChange = (value: string) => {
    setStatus(value as OrderStatus | 'all');
    setPage(1); // Reset to first page on filter change
  };

  const handleRowClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleCreateOrder = () => {
    navigate('/orders/create');
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600">Error loading orders: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Button */}
      <div className="flex items-center justify-end">
        <Button onClick={handleCreateOrder}>
          <Plus className="h-4 w-4 mr-2" />
          New Order
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by property address..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Property Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Target Close</TableHead>
              <TableHead>Sale Price</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  Loading orders...
                </TableCell>
              </TableRow>
            ) : !data?.orders?.length ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="text-muted-foreground">
                    {search || status !== 'all' ? 'No orders match your filters' : 'No orders yet'}
                  </div>
                  {!search && status === 'all' && (
                    <Button variant="link" onClick={handleCreateOrder} className="mt-2">
                      Create your first order
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              data.orders.map((order) => (
                <TableRow
                  key={order.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(order.id)}
                >
                  <TableCell className="font-medium">{order.order_number}</TableCell>
                  <TableCell>
                    <div>
                      <div>{order.property_address}</div>
                      <div className="text-sm text-muted-foreground">
                        {order.property_city}, {order.property_state} {order.property_zip}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                        order.status,
                      )}`}
                    >
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(order.target_closing_date)}</TableCell>
                  <TableCell>
                    {order.sale_price
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                        }).format(Number(order.sale_price))
                      : '-'}
                  </TableCell>
                  <TableCell>{formatDate(order.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, data.pagination.total)} of{' '}
            {data.pagination.total} orders
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {data.pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
